/**
 * useKineticCapture — Self-contained camera + pose + rep-detection hook.
 *
 * Wraps the three pieces the legacy KtsMount glued together inline:
 *   1. getUserMedia camera setup → attaches stream to a video element
 *   2. useMediaPipePose — runs PoseLandmarker per frame, yields landmarks
 *   3. HeuristicEngine — stateful rep detector (DOWN→UP cycle with hysteresis)
 *
 * On a detected rep, fires `onRep` with a canonical event shape that
 * the phased session hook (`useKtsPhasedSession`) can pass straight to
 * `emitEvent()`. No KTS coupling here — this hook only knows about poses
 * and reps.
 *
 * Lifecycle gates:
 *   - `enabled = false` → camera off, pose off, engine reset
 *   - `enabled = true`  → camera on, pose detection running, engine active
 *
 * Designed to be plugged into KtsPhasedMount.ActionSurface (replacing the
 * demo "Tap to log rep" button) and reusable by any future kinetic UI.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaPipePose } from './useMediaPipePose';
import {
  HeuristicEngine,
  type HeuristicRule,
  type RepEvent,
} from '../lib/heuristic-engine';
import {
  angleBetweenPoints,
  LANDMARK,
  type NormalizedLandmark,
} from '../lib/mediapipe-pose';
import type { CanonicalActionEvent } from '../lib/kts-v1-client';

// ───────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────

export interface UseKineticCaptureInput {
  /** Whether to actively capture (camera on, pose running). */
  enabled: boolean;
  /** Tool's heuristic_rule (joint angles + thresholds). When omitted, no
   *  rep detection runs but the camera still attaches for visual feedback. */
  rule?: HeuristicRule | null;
  /** Called once per detected rep with a canonical event ready for emitEvent. */
  onRep?: (event: CanonicalActionEvent) => void;
  /** Called on each pose frame with raw landmarks — useful for skeleton overlays. */
  onPoseFrame?: (landmarks: NormalizedLandmark[]) => void;
}

export interface UseKineticCapture {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  cameraReady: boolean;
  poseLoading: boolean;
  poseReady: boolean;
  cameraError: string | null;
  poseError: string | null;
  /** Current rep state machine phase ('waiting' | 'down'). */
  repPhase: 'waiting' | 'down';
  /** Live rep count tracked by the engine. */
  repCount: number;
  /** Live joint angles for debug HUD (left, right, average). */
  debugAngles: { L: number; R: number; avg: number } | null;
  /** Manual reset — clears engine state for a fresh action phase. */
  reset: () => void;
}

// ───────────────────────────────────────────────────────────────────────────
// Hook
// ───────────────────────────────────────────────────────────────────────────

export function useKineticCapture(input: UseKineticCaptureInput): UseKineticCapture {
  const { enabled, rule, onRep, onPoseFrame } = input;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const engineRef = useRef<HeuristicEngine | null>(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [repPhase, setRepPhase] = useState<'waiting' | 'down'>('waiting');
  const [repCount, setRepCount] = useState(0);
  const [debugAngles, setDebugAngles] = useState<{ L: number; R: number; avg: number } | null>(null);

  // Stable refs so the per-frame callback closes over the freshest fns
  const onRepRef = useRef(onRep);
  onRepRef.current = onRep;
  const onPoseFrameRef = useRef(onPoseFrame);
  onPoseFrameRef.current = onPoseFrame;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // ── Build / rebuild the engine when the rule changes ──────────────────
  useEffect(() => {
    if (!rule) {
      engineRef.current = null;
      return;
    }
    engineRef.current = new HeuristicEngine(rule);
    setRepCount(0);
    setRepPhase('waiting');
  }, [rule]);

  // ── Camera setup ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      setCameraReady(false);
      return;
    }
    let stream: MediaStream | null = null;
    let cancelled = false;

    (async () => {
      // Pre-flight: getUserMedia requires a secure context (HTTPS or
      // localhost). On http:// it silently rejects with NotAllowedError or
      // is undefined entirely. Surface that explicitly so the user knows
      // it's a context issue, not a permissions one.
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        setCameraError(
          'Camera requires HTTPS. Open this page over a secure connection.',
        );
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Camera API not available in this browser.');
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        // Wait for the video element to mount. The ref may be null on the
        // very first effect tick depending on render order; retry briefly.
        let attempts = 0;
        while (!videoRef.current && attempts < 20) {
          await new Promise((r) => setTimeout(r, 50));
          attempts++;
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
        }
        const video = videoRef.current;
        if (!video) {
          console.warn('[useKineticCapture] videoRef never attached — releasing stream');
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        video.srcObject = stream;

        // Some browsers (notably Safari/iOS, occasionally Chrome with
        // restrictive autoplay policies) require an explicit .play() call
        // even when the <video> has the autoplay attribute. We mute the
        // element to keep autoplay allowed regardless.
        try {
          await video.play();
        } catch (playErr) {
          // Not fatal — the autoplay attribute usually still gets it going.
          console.warn('[useKineticCapture] video.play() rejected (will rely on autoplay):', playErr);
        }

        // Wait for first frame metadata so width/height are non-zero.
        if (video.readyState < 1) {
          await new Promise<void>((resolve) => {
            const onReady = () => {
              video.removeEventListener('loadedmetadata', onReady);
              resolve();
            };
            video.addEventListener('loadedmetadata', onReady);
            // Belt-and-suspenders timeout — don't hang forever
            setTimeout(() => {
              video.removeEventListener('loadedmetadata', onReady);
              resolve();
            }, 3000);
          });
        }

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        setCameraReady(true);
        setCameraError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('[useKineticCapture] camera access denied', err);
        const name = (err as { name?: string })?.name ?? '';
        const msg = err instanceof Error ? err.message : 'Camera access denied';
        setCameraError(
          name === 'NotAllowedError'
            ? 'Camera permission denied. Allow camera access in your browser settings and reload.'
            : name === 'NotFoundError'
              ? 'No camera found on this device.'
              : msg,
        );
      }
    })();

    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
      setCameraReady(false);
    };
  }, [enabled]);

  // ── Per-frame handler — passes landmarks through engine + debug HUD ───
  const frameTickRef = useRef(0);
  const handleFrame = useCallback((landmarks: NormalizedLandmark[]) => {
    onPoseFrameRef.current?.(landmarks);

    if (!enabledRef.current) return;
    const engine = engineRef.current;
    if (!engine) return;

    const event: RepEvent | null = engine.evaluate(landmarks);
    setRepPhase(engine.currentPhase);

    // Throttle debug-angle updates to ~3hz so React doesn't re-render every frame
    frameTickRef.current++;
    if (frameTickRef.current % 10 === 0) {
      try {
        const left = angleBetweenPoints(
          landmarks[LANDMARK.LEFT_SHOULDER],
          landmarks[LANDMARK.LEFT_ELBOW],
          landmarks[LANDMARK.LEFT_WRIST],
        );
        const right = angleBetweenPoints(
          landmarks[LANDMARK.RIGHT_SHOULDER],
          landmarks[LANDMARK.RIGHT_ELBOW],
          landmarks[LANDMARK.RIGHT_WRIST],
        );
        setDebugAngles({
          L: Math.round(left),
          R: Math.round(right),
          avg: Math.round((left + right) / 2),
        });
      } catch {
        // Ignore — landmarks may be partially undefined on edge frames
      }
    }

    if (event) {
      setRepCount(engine.reps);
      const canonical: CanonicalActionEvent = {
        action: 'rep_complete',
        timestamp_ms: event.timestamp_ms,
        score: event.confidence_score,
        reinforced: true, // engine output is post-validation; treat as reinforced
        joint_angles: event.joint_data.angles,
      };
      onRepRef.current?.(canonical);
    }
  }, []);

  // ── Pose pipeline (lazy WASM, ~30fps) ─────────────────────────────────
  const { loading: poseLoading, ready: poseReady, error: poseError } =
    useMediaPipePose({
      videoRef,
      enabled,
      onFrame: handleFrame,
    });

  // ── Manual reset ──────────────────────────────────────────────────────
  const reset = useCallback(() => {
    engineRef.current?.reset();
    setRepCount(0);
    setRepPhase('waiting');
    setDebugAngles(null);
  }, []);

  return useMemo(
    () => ({
      videoRef,
      cameraReady,
      poseLoading,
      poseReady,
      cameraError,
      poseError,
      repPhase,
      repCount,
      debugAngles,
      reset,
    }),
    [cameraReady, poseLoading, poseReady, cameraError, poseError, repPhase, repCount, debugAngles, reset],
  );
}
